import { useTranslation } from "react-i18next";
import {
  AppLayout,
  AppSidebar,
  BundleHeader,
  EmptyState,
  LoadingPage,
  PageHeader,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { useEvents } from "./useEvents";
import { BoxIso } from "iconoir-react";
import { getMultilingualString } from "shared/utils";
import { EventCard } from "shared/components/organisms/EventCard/EventCard";
import { useLoggedInAs } from "shared/hooks";
import {
  EnumEventAccessOperatorStatus,
  EnumOwnerType,
  type AssetImage,
} from "gql/generated";
import { EventCreateEditModal } from "../../shared/components/organisms/EventCreationAndEditing/EventCreateEditModal";
import { useAppContext } from "AppContext";

export const Events = () => {
  const { t } = useTranslation("events", { keyPrefix: "APP" });

  // The EventCreateEditModal needs to know the organization ID to fetch the correct tags
  const loggedInAs = useLoggedInAs();
  const organizationId =
    loggedInAs?.type === EnumOwnerType.Organization
      ? loggedInAs?.entityId
      : undefined;
  const { dbUser, operators } = useAppContext();

  const defaultOrganizationId =
    organizationId ?? operators[0]?.enrollments[0]?.organization?._id;
  const defaultOperatorEntityIds = operators.map(
    (operator) => operator._entityId,
  );

  // TODO: Renable export to CSV after the event widget changes are completed
  const {
    events,
    loading,
    error,
    appViewSections,
    // isLoadingOperatorCsvData,
    searchValue,
    selectedAppItem,
    isModalOpen,
    modalMode,
    eventId,
    handleModalOpen,
    onAppViewChange,
    // onGetEventCsvData,
    onSearchValueChange,
    setIsModalOpen,
    updateOrAddEvent,
    removeEvent,
  } = useEvents();

  // const csvFilename = "wb-events";
  // const csvColumnHeaders = getEventCSVHeaders(t);

  if (loading) return <LoadingPage />;

  if (error) throw new Error(error.message);

  return (
    <>
      <AppLayout
        sidebar={
          <AppSidebar
            Icon={BoxIso}
            headerTitle={t("HEADER.TITLE")}
            headerDescription={t("HEADER.DESCRIPTION")}
            primaryButtonCopy={t("HEADER.CTA_BUTTON_COPY")}
            primaryButtonProps={{
              visual: "accent.blue",
              onClick: () => handleModalOpen("create"),
            }}
            viewTitle={t("HEADER.VIEWS.TITLE")}
            viewSections={appViewSections}
            onViewSelectionChange={onAppViewChange}
          />
        }
        content={
          <>
            <PageHeader
              Icon={selectedAppItem.Icon}
              title={t(`HEADER.VIEWS.${selectedAppItem.key}.TITLE`)}
              description={t(`HEADER.VIEWS.${selectedAppItem.key}.DESCRIPTION`)}
              /*
              ctaButtonReplacement={
                <ExportToCSV
                  colHeaders={csvColumnHeaders}
                  filename={csvFilename}
                  buttonProps={{
                    mode: "light",
                    width: "auto",
                    isLoading: isLoadingOperatorCsvData,
                  }}
                  onGetData={onGetEventCsvData}
                />
              }
              */
            />
            <BundleHeader
              searchProps={{
                value: searchValue,
                onValueChange: onSearchValueChange,
              }}
              count={events.length}
              Icon={selectedAppItem.Icon}
              title={t(`HEADER.VIEWS.${selectedAppItem.key}.TITLE`)}
            />
            <Flex flexDir="column" gap="150" flexGrow={1} marginTop="400">
              {events.length < 1 ? (
                <EmptyState text={t("PAGE.EMPTY")} />
              ) : (
                events.map((event) => {
                  const status = event.access.organizations.find(
                    (org) => org?._entityId === organizationId,
                  )?.status;

                  const btnText =
                    status === EnumEventAccessOperatorStatus.ForReview
                      ? t("CARD.CTA.APPROVE")
                      : t("CARD.CTA.REVIEW");

                  return (
                    <EventCard
                      key={event._id}
                      title={getMultilingualString(event.title)}
                      hero={event.images[0] as AssetImage}
                      updatedDate={event.updatedDateISO}
                      status={status}
                      actionButtonProps={{
                        children: `${btnText}...`,
                        onClick: () =>
                          handleModalOpen(
                            status === EnumEventAccessOperatorStatus.ForReview
                              ? "approve"
                              : "edit",
                            event._id,
                          ),
                      }}
                    />
                  );
                })
              )}
            </Flex>
          </>
        }
      />
      <EventCreateEditModal
        isOpen={isModalOpen}
        userEmailAddress={dbUser?.email ?? ""}
        userType={loggedInAs?.type ?? "PUBLIC"}
        initialMode={modalMode}
        defaultOrganizationId={defaultOrganizationId}
        defaultOperatorEntityIds={defaultOperatorEntityIds}
        eventId={eventId}
        onClose={() => setIsModalOpen(false)}
        onEventUpdated={updateOrAddEvent}
        onEventDeleted={removeEvent}
      />
    </>
  );
};
